import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { clamp, type Vector2 } from '../match/Vector2';

export class SteeringSystem {
  update(
    player: PlayerRuntimeState,
    deltaTime: number,
    maximumSpeed: number,
    acceleration: number,
    avoidance: Vector2 = { x: 0, y: 0 },
    turnRateRadiansPerSecond = Number.POSITIVE_INFINITY,
    brakingAccelerationMultiplier = 1,
  ): void {
    const offsetX = player.targetPosition.x - player.position.x;
    const offsetY = player.targetPosition.y - player.position.y;
    const remaining = Math.hypot(offsetX, offsetY);
    const hasTarget = remaining >= .16;
    let directionX = hasTarget ? offsetX / remaining : 0;
    let directionY = hasTarget ? offsetY / remaining : 0;
    if (Math.hypot(avoidance.x, avoidance.y) > .001) {
      directionX += avoidance.x * 1.08;
      directionY += avoidance.y * 1.08;
      const combinedLength = Math.hypot(directionX, directionY);
      if (combinedLength > .001) {
        directionX /= combinedLength;
        directionY /= combinedLength;
      }
    }
    const desiredDirection = { x: directionX, y: directionY };
    const canReact = player.reactionSecondsRemaining <= 0;
    if (canReact && hasTarget) {
      player.facingDirection = this.rotateTowards(
        player.facingDirection,
        desiredDirection,
        turnRateRadiansPerSecond * deltaTime,
      );
    }
    player.reactionSecondsRemaining = Math.max(0, player.reactionSecondsRemaining - deltaTime);

    // Ease into the final metres of a run. Apart from looking more natural,
    // this removes the one-frame direction flips responsible for jitter.
    const currentSpeed = Math.hypot(player.velocity.x, player.velocity.y);
    // During reaction time a moving player coasts on the previous command;
    // a stationary player does not start running along an obsolete facing.
    const arrivalSpeed = canReact
      ? Math.min(maximumSpeed, remaining * 1.25)
      : Math.min(currentSpeed, maximumSpeed);
    const steeringDirection = hasTarget ? player.facingDirection : { x: 0, y: 0 };
    const desiredX = steeringDirection.x * arrivalSpeed;
    const desiredY = steeringDirection.y * arrivalSpeed;
    const velocityDx = desiredX - player.velocity.x;
    const velocityDy = desiredY - player.velocity.y;
    const velocityDelta = Math.hypot(velocityDx, velocityDy);
    const desiredSpeed = Math.hypot(desiredX, desiredY);
    const directionDot = currentSpeed > .001 && desiredSpeed > .001
      ? (player.velocity.x * desiredX + player.velocity.y * desiredY) / (currentSpeed * desiredSpeed)
      : 1;
    const braking = desiredSpeed < currentSpeed || directionDot < .25;
    const maximumVelocityDelta = acceleration * (braking ? brakingAccelerationMultiplier : 1) * deltaTime;
    const velocityAmount = velocityDelta <= maximumVelocityDelta || velocityDelta === 0 ? 1 : maximumVelocityDelta / velocityDelta;
    player.velocity = {
      x: player.velocity.x + velocityDx * velocityAmount,
      y: player.velocity.y + velocityDy * velocityAmount,
    };
    const displacementX = player.velocity.x * deltaTime;
    const displacementY = player.velocity.y * deltaTime;
    const displacementTowardsTarget = remaining > .001
      ? (displacementX * offsetX + displacementY * offsetY) / remaining
      : 0;
    if (displacementTowardsTarget > remaining) {
      player.position = { ...player.targetPosition };
    } else {
      player.position = {
        x: clamp(player.position.x + displacementX, 1, 99),
        y: clamp(player.position.y + displacementY, 2, 98),
      };
    }
  }

  private rotateTowards(current: Vector2, desired: Vector2, maximumAngle: number): Vector2 {
    const currentAngle = Math.atan2(current.y, current.x);
    const desiredAngle = Math.atan2(desired.y, desired.x);
    let delta = desiredAngle - currentAngle;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    const angle = currentAngle + clamp(delta, -maximumAngle, maximumAngle);
    return { x: Math.cos(angle), y: Math.sin(angle) };
  }
}
